/** Image preparation is local to this tab; no source image is uploaded or retained. */
export interface SignatureRaster { width: number; height: number; data: Uint8ClampedArray }
export type SignatureImageErrorCode = 'unsupported' | 'too_large' | 'unreadable' | 'no_ink' | 'too_dense';
export class SignatureImageError extends Error {
  constructor(public readonly code: SignatureImageErrorCode) { super(code); this.name = 'SignatureImageError'; }
}
const fail = (code: SignatureImageErrorCode): never => { throw new SignatureImageError(code); };
const MAX_FILE = 10 * 1024 * 1024;
const MAX_PIXELS = 40 * 1000 * 1000;

/** Read dimensions before asking the browser to allocate a decoded raster. */
function dimensions(bytes: Uint8Array, type: string): [number, number] {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  const ascii = (at: number, count: number) => String.fromCharCode(...bytes.subarray(at, at + count));
  if (type === 'image/png' && bytes.length >= 33 && ascii(0, 8) === '\x89PNG\r\n\x1a\n' && ascii(12, 4) === 'IHDR') {
    return [view.getUint32(16), view.getUint32(20)];
  }
  if (type === 'image/jpeg' && bytes[0] === 255 && bytes[1] === 216) {
    let at = 2;
    while (at + 3 < bytes.length) {
      if (bytes[at++] !== 255) break;
      while (bytes[at] === 255) at++;
      const marker = bytes[at++];
      if (marker === 217 || marker === 218) break;
      if (marker === 1 || (marker >= 208 && marker <= 215)) continue;
      if (at + 2 > bytes.length) break;
      const length = view.getUint16(at);
      if (length < 2 || at + length > bytes.length) break;
      if ([192, 193, 194, 195, 197, 198, 199, 201, 202, 203, 205, 206, 207].includes(marker) && length >= 8) {
        return [view.getUint16(at + 5), view.getUint16(at + 3)];
      }
      at += length;
    }
  }
  if (type === 'image/webp' && bytes.length >= 30 && ascii(0, 4) === 'RIFF' && ascii(8, 4) === 'WEBP') {
    const chunk = ascii(12, 4);
    const uint24 = (at: number) => bytes[at]! + bytes[at + 1]! * 256 + bytes[at + 2]! * 65536;
    if (chunk === 'VP8X') return [uint24(24) + 1, uint24(27) + 1];
    if (chunk === 'VP8 ' && ascii(23, 3) === '\x9d\x01\x2a') return [view.getUint16(26, true) & 16383, view.getUint16(28, true) & 16383];
    if (chunk === 'VP8L' && bytes[20] === 47) {
      const packed = view.getUint32(21, true);
      return [(packed & 16383) + 1, ((packed >>> 14) & 16383) + 1];
    }
  }
  return fail('unreadable');
}
function validDimensions(width: number, height: number, limit: number): void {
  if (!Number.isSafeInteger(width) || !Number.isSafeInteger(height) || width < 1 || height < 1) fail('unreadable');
  if (width * height > limit || width > 65535 || height > 65535) fail('too_large');
}
function canvas(width: number, height: number): HTMLCanvasElement {
  const result = document.createElement('canvas'); result.width = width; result.height = height; return result;
}

export async function readSignatureImage(file: File): Promise<SignatureRaster> {
  if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) fail('unsupported');
  if (file.size > MAX_FILE) fail('too_large');
  let bitmap: ImageBitmap | undefined;
  let url: string | undefined;
  try {
    const bytes = new Uint8Array(await file.arrayBuffer());
    const [width, height] = dimensions(bytes, file.type);
    validDimensions(width, height, MAX_PIXELS);
    let source: CanvasImageSource;
    let decodedWidth: number; let decodedHeight: number;
    if (typeof createImageBitmap === 'function') {
      bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      source = bitmap; decodedWidth = bitmap.width; decodedHeight = bitmap.height;
    } else {
      url = URL.createObjectURL(file);
      const img = new Image(); img.src = url; await img.decode();
      source = img; decodedWidth = img.naturalWidth; decodedHeight = img.naturalHeight;
    }
    validDimensions(decodedWidth, decodedHeight, MAX_PIXELS);
    const scale = Math.min(1, 1600 / Math.max(decodedWidth, decodedHeight), Math.sqrt(1_000_000 / (decodedWidth * decodedHeight)));
    const target = canvas(Math.max(1, Math.floor(decodedWidth * scale)), Math.max(1, Math.floor(decodedHeight * scale)));
    const ctx = target.getContext('2d'); if (!ctx) return fail('unreadable');
    ctx.drawImage(source, 0, 0, target.width, target.height);
    return { width: target.width, height: target.height, data: ctx.getImageData(0, 0, target.width, target.height).data };
  } catch (error) {
    if (error instanceof SignatureImageError) throw error;
    return fail('unreadable');
  } finally { bitmap?.close(); if (url) URL.revokeObjectURL(url); }
}

export function cleanSignatureImage(raster: SignatureRaster, threshold = 190): { png: string; width: number; height: number } {
  try {
    const { width, height, data } = raster;
    validDimensions(width, height, 1_000_000);
    if (!(data instanceof Uint8ClampedArray) || data.length !== width * height * 4 || !Number.isFinite(threshold)) return fail('unreadable');
    threshold = Math.min(240, Math.max(80, threshold));
    const pixels = new Uint8ClampedArray(data.length);
    let minX = width; let minY = height; let maxX = -1; let maxY = -1; let ink = 0; let retainedPixels = 0;
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const at = (y * width + x) * 4;
      const luminance = 0.2126 * data[at]! + 0.7152 * data[at + 1]! + 0.0722 * data[at + 2]!;
      const alpha = Math.round(data[at + 3]! * Math.min(1, Math.max(0, (threshold - luminance) / 28)));
      if (alpha < 12) continue;
      pixels[at] = data[at]!; pixels[at + 1] = data[at + 1]!; pixels[at + 2] = data[at + 2]!; pixels[at + 3] = alpha;
    }
    const visited = new Uint8Array(width * height);
    const queue = new Int32Array(width * height);
    let largest = 0;
    for (let start = 0; start < visited.length; start++) {
      if (visited[start] || !pixels[start * 4 + 3]) continue;
      let head = 0; let tail = 1; queue[0] = start; visited[start] = 1;
      while (head < tail) {
        const index = queue[head++]!; const x = index % width; const y = Math.floor(index / width);
        for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
          const nx = x + dx; const ny = y + dy;
          if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
          const next = ny * width + nx;
          if (!visited[next] && pixels[next * 4 + 3]) { visited[next] = 1; queue[tail++] = next; }
        }
      }
      largest = Math.max(largest, tail);
      if (tail < 4) for (let i = 0; i < tail; i++) pixels[queue[i]! * 4 + 3] = 0;
    }
    for (let y = 0; y < height; y++) for (let x = 0; x < width; x++) {
      const alpha = pixels[(y * width + x) * 4 + 3]!;
      if (!alpha) continue;
      retainedPixels++;
      ink += alpha / 255;
      minX = Math.min(minX, x); maxX = Math.max(maxX, x); minY = Math.min(minY, y); maxY = Math.max(maxY, y);
    }
    // Reject empty scans and isolated dust; this is a preview aid, not handwriting recognition.
    if (largest < 8 || ink < 12 || maxX - minX < 5 || maxY - minY < 2) return fail('no_ink');
    // Soft alpha must not make a uniformly shaded sheet appear sparsely inked.
    if (retainedPixels / (width * height) > 0.55 || retainedPixels / ((maxX - minX + 1) * (maxY - minY + 1)) > 0.8) return fail('too_dense');
    const pad = 4;
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(width - 1, maxX + pad); maxY = Math.min(height - 1, maxY + pad);
    const cropped = canvas(maxX - minX + 1, maxY - minY + 1);
    const ctx = cropped.getContext('2d'); if (!ctx) return fail('unreadable');
    const image = ctx.createImageData(cropped.width, cropped.height);
    for (let y = 0; y < cropped.height; y++) {
      image.data.set(pixels.subarray(((y + minY) * width + minX) * 4, ((y + minY) * width + maxX + 1) * 4), y * cropped.width * 4);
    }
    ctx.putImageData(image, 0, 0);
    let target = cropped;
    let longest = 1200;
    for (let attempt = 0; attempt < 5; attempt++) {
      const scale = Math.min(1, longest / Math.max(cropped.width, cropped.height));
      if (scale < 1) {
        target = canvas(Math.max(1, Math.floor(cropped.width * scale)), Math.max(1, Math.floor(cropped.height * scale)));
        const targetCtx = target.getContext('2d'); if (!targetCtx) return fail('unreadable');
        targetCtx.drawImage(cropped, 0, 0, target.width, target.height);
      }
      const png = target.toDataURL('image/png');
      if (!png.startsWith('data:image/png;base64,')) return fail('unreadable');
      const base64 = png.slice(22);
      if (Math.floor(base64.length * 3 / 4) - (base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0) <= 1024 * 1024) {
        return { png, width: target.width, height: target.height };
      }
      longest = Math.floor(Math.max(target.width, target.height) * 0.7);
    }
    return fail('too_large');
  } catch (error) {
    if (error instanceof SignatureImageError) throw error;
    return fail('unreadable');
  }
}
